import { CourseApi, KlassApi, PrivateKlassApi } from '@app/api';
import { Course } from '@app/model/Course';
import { KLASS_TYPE } from '@app/model/Enums';
import { Klass, PrivateKlass } from '@app/model/Klass';
import { useCourseStore, useKlassStore } from './entityStore';

/**
 * Looking an entity up by something other than its id, answering from the store's cache when
 * it can and from the network when it cannot.
 *
 * This is the layer that gets to know about both halves, and the reason it exists as its own
 * module: '@app/api' is transport and must not reach up into the stores, while the stores are
 * built out of the repositories. A reference in both directions is a cycle, and in a cycle the
 * module evaluated second sees the first one's exports as undefined — which is exactly what
 * happened while these lookups lived in api/index.ts. The app only worked because the route
 * tree happened to reach entityStore first; importing a single page in isolation (a test, a
 * lazily-loaded route) came up with every store holding an undefined repository.
 *
 * Nothing imports this module back, so the dependency runs one way: api -> store -> lookups.
 *
 * `bypassCache` is what a Refresh action passes: answer from the server whatever is held.
 */

/** A course by its SKU. Matched case-insensitively, as SKUs are quoted both ways. */
export const loadCourseBySku = async (sku: string, bypassCache = false): Promise<Course> => {
  if (!bypassCache) {
    const cached = useCourseStore.getState().items.find(c => c.sku?.toLowerCase() === sku.toLowerCase());
    if (cached) return cached;
  }
  return CourseApi.getBySku(sku);
};

/**
 * Every course that is a certification.
 *
 * Gated on `isLoaded` rather than on the cache merely being non-empty: filtering a store that
 * holds a handful of individually-fetched courses would quietly answer with a fraction of the
 * certifications, and the caller has no way to tell that apart from there being no more.
 */
export const loadCertifications = async (bypassCache = false): Promise<Course[]> => {
  const { isLoaded, items } = useCourseStore.getState();
  if (!bypassCache && isLoaded) return items.filter(c => c.isCertification);

  return CourseApi.getCertifications();
};

/** A class by the offering id the rest of the business quotes it by. */
export const loadKlassByOfferingId = async (oid: string | number, bypassCache = false): Promise<Klass> => {
  if (!bypassCache) {
    const cached = useKlassStore.getState().items.find(k => String(k.offeringId) === String(oid));
    if (cached) return cached;
  }
  return KlassApi.getByOfferingId(oid);
};

/** As loadKlassByOfferingId, for a caller that needs the private class fields. */
export const loadPrivateKlassByOfferingId = async (oid: string | number, bypassCache = false): Promise<PrivateKlass> => {
  if (!bypassCache) {
    const cached = useKlassStore.getState().items.find(k => String(k.offeringId) === String(oid));
    if (cached) return cached as PrivateKlass;
  }
  return PrivateKlassApi.getByOfferingId(oid);
};

/**
 * A private class by its request id, the `REQnnnn` form.
 *
 * Private classes are not the only ones carrying a request id any more - an ad hoc class holds an
 * `ADRnnnn` - so this is the lookup for when the kind is known. When it is not,
 * `KlassApi.search({ requestId: [...] })` matches both.
 */
export const loadPrivateKlassByRequestId = async (reqId: string | number, bypassCache = false): Promise<PrivateKlass> => {
  if (!bypassCache) {
    const cached = useKlassStore.getState().items.find(
      k => k.type === KLASS_TYPE.PRIVATE && String((k as PrivateKlass).requestId) === String(reqId)
    );
    if (cached) return cached as PrivateKlass;
  }
  return PrivateKlassApi.getByRequestId(reqId);
};
